import React, { useEffect, useMemo, useState } from "react";
import "../../styles/VendorOnboarding.css";
import SearchIcon from "../../icons/SearchIcon";
import PendingIcon from "../../icons/Arrows/pendingIcon";
import ApprovedIcon from "../../icons/ApprovedIconGreen";
import DocumentIcon from "../../icons/Arrows/DocumentIcon";
import VendorReviewView from "../../views/VendorReviewView";
import companyController from "../../controllers/companyController";
import { userService } from "../../services/userService";
import { translateLabel, translateText, useTranslationSnapshot } from "./../../translations/runtime";


const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

const BUSINESS_TYPES = [
  "GROCERIES",
  "RESTAURANTS",
];

const REQUEST_STATUSES = ["PENDING", "APPROVED", "REJECTED", "RESUBMIT"];

const getStoredToken = () => {
  const storedUser = userService.getJwt();

  if (typeof storedUser === "string") return storedUser;

  return (
    storedUser?.jwt ||
    storedUser?.token ||
    storedUser?.accessToken ||
    storedUser?.access_token ||
    ""
  );
};

const getPayload = (response) => {
  const payload = response?.data;
  return payload?.data ?? payload?.result ?? payload;
};

const toDisplayLabel = (value) => {
  if (!value) return "-";

  return String(value)
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatSubmissionDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
};

const isPresent = (value) =>
  value !== undefined && value !== null && value !== "";

const firstDefined = (...values) => values.find(isPresent) || "";

const getBusinessTypeLabel = (request = {}) => {
  const descriptiveText = [
    request.businessTypeLabel,
    request.businessProfileTypeName,
    request.businessProfileType,
    request.businessName,
    request.businessDescription,
    request.description,
    request.companyDescription,
    request.businessProfile?.businessDescription,
    request.businessProfile?.description,
    request.company?.businessDescription,
    request.company?.description,
    request.companyDetails?.businessDescription,
    request.companyDetails?.description,
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/[_-]+/g, " ")
    .toLowerCase();

  if (
    descriptiveText.includes("restaurant") ||
    descriptiveText.includes("cuisine")
  ) {
    return "Restaurant";
  }

  if (descriptiveText.includes("grocery") || descriptiveText.includes("grocer")) {
    return "Groceries";
  }

  const rawType = firstDefined(
    request.businessProfileTypeName,
    request.businessProfileType,
    request.businessProfile?.businessProfileTypeName,
    request.businessProfile?.businessProfileType,
    request.company?.businessProfileTypeName,
    request.company?.businessProfileType,
    request.companyDetails?.businessProfileTypeName,
    request.companyDetails?.businessProfileType,
    request.category,
    request.categoryName,
    request.businessCategory,
    request.businessProfileCategoryName,
    request.businessProfile?.categoryName,
    request.businessProfile?.businessProfileCategoryName,
    request.company?.categoryName,
    request.company?.businessProfileCategoryName,
    request.companyDetails?.categoryName,
    request.companyDetails?.businessProfileCategoryName,
    request.businessTypeLabel,
    request.businessType,
  );
  const normalized = String(rawType || "")
    .replace(/[_-]+/g, " ")
    .toLowerCase();

  if (normalized.includes("restaurant") || normalized.includes("cuisine")) {
    return "Restaurant";
  }

  if (normalized.includes("grocery") || normalized.includes("grocer")) {
    return "Groceries";
  }

  return toDisplayLabel(rawType);
};

const getRequestCompanyId = (request = {}) =>
  firstDefined(
    request.companyID,
    request.companyId,
    request.company_id,
    request.company?.companyID,
    request.company?.companyId,
    request.company?.id,
    request.companyDetails?.companyID,
    request.companyDetails?.companyId,
    request.companyDetails?.id,
  );

const getRequestBusinessProfileId = (request = {}) =>
  firstDefined(
    request.businessProfileID,
    request.businessProfileId,
    request.busienssProfileID,
    request.busienssProfileId,
    request.business_profile_id,
    request.businessProfile?.businessProfileID,
    request.businessProfile?.businessProfileId,
    request.businessProfile?.busienssProfileID,
    request.businessProfile?.busienssProfileId,
    request.businessProfile?.id,
    request.company?.businessProfileID,
    request.company?.businessProfileId,
    request.company?.busienssProfileID,
    request.company?.busienssProfileId,
    request.companyDetails?.businessProfileID,
    request.companyDetails?.businessProfileId,
    request.companyDetails?.busienssProfileID,
    request.companyDetails?.busienssProfileId,
  );

const getRequestApplicationId = (request = {}) =>
  firstDefined(
    request.applicationID,
    request.applicationId,
    request.application_id,
    request.requestID,
    request.requestId,
    request.companyRequestID,
    request.companyRequestId,
  );

const isSameRequest = (request = {}, updatedRow = {}) => {
  const requestApplicationId = getRequestApplicationId(request);
  const updatedApplicationId = getRequestApplicationId(updatedRow);
  const requestCompanyId = getRequestCompanyId(request);
  const updatedCompanyId = getRequestCompanyId(updatedRow);

  if (requestApplicationId && updatedApplicationId) {
    return requestApplicationId === updatedApplicationId;
  }

  if (requestCompanyId && updatedCompanyId) {
    return requestCompanyId === updatedCompanyId;
  }

  return request.id && updatedRow.id && request.id === updatedRow.id;
};

const normalizeRequest = (request, index) => ({
  ...request,
  applicationId: getRequestApplicationId(request),
  companyID: getRequestCompanyId(request),
  companyId: getRequestCompanyId(request),
  businessProfileID: getRequestBusinessProfileId(request),
  businessProfileId: getRequestBusinessProfileId(request),
  id: request.requestId || getRequestCompanyId(request) || index,
  storeName: request.storeName || request.companyName || "-",
  businessName: getBusinessTypeLabel(request),
  owner: request.ownerName || request.applicantName || "-",
  submitted: formatSubmissionDate(request.submissionDate),
  documents: request.documentsCount ?? 0,
  status: toDisplayLabel(request.requestStatus),
});

const StatusBadge = ({ status }) => {
  useTranslationSnapshot();
  if (status === "Pending") {
    return (
      <span className="business-request-status business-request-status-pending">
        <PendingIcon /> {translateLabel("txt_common_pending", "Pending")} </span>
    );
  }

  if (status === "Approved") {
    return (
      <span className="business-request-status business-request-status-approved">
        <ApprovedIcon /> {translateLabel("txt_business_approved", "Approved")} </span>
    );
  }

  if (status === "Rejected") {
    return (
      <span className="business-request-status business-request-status-rejected"> {translateLabel("txt_admin_rejected", "Rejected")} </span>
    );
  }

  if (status === "Resubmit") {
    return (
      <span className="business-request-status business-request-status-resubmit">
        Resubmit
      </span>
    );
  }

  return <span className="business-request-status">{status}</span>;
};

function VendorOnboarding() {
  useTranslationSnapshot();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [requests, setRequests] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeView, setActiveView] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchCompanyRequests = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await companyController.getCompanyRequests({
          businessType,
          page,
          search: debouncedSearch,
          size: rowsPerPage,
          status,
          jwt: getStoredToken(),
        });

        if (
          response instanceof Error ||
          response?.isAxiosError ||
          response?.response
        ) {
          throw response;
        }

        const payload = getPayload(response);
        const rows = Array.isArray(payload?.requests) ? payload.requests : [];
        const records = Number(payload?.totalRecords ?? rows.length);
        const pages = Number(
          payload?.totalPages ?? Math.ceil(records / rowsPerPage),
        );

        if (!isCurrentRequest) return;

        setRequests(rows.map(normalizeRequest));
        setDashboard(payload?.dashboard || {});
        setTotalRecords(records);
        setTotalPages(pages);
      } catch (requestError) {
        if (!isCurrentRequest) return;

        setRequests([]);
        setDashboard({});
        setTotalRecords(0);
        setTotalPages(0);
        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load business requests.",
        );
      } finally {
        if (isCurrentRequest) setLoading(false);
      }
    };

    fetchCompanyRequests();

    return () => {
      isCurrentRequest = false;
    };
  }, [businessType, debouncedSearch, page, rowsPerPage, status]);

  useEffect(() => {
    const onPop = () => {
      setActiveView(null);
    };

    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const visiblePages = useMemo(() => {
    if (totalPages <= 4) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = new Set([1, totalPages, page - 1, page, page + 1]);
    return [...pages]
      .filter((pageNumber) => pageNumber >= 1 && pageNumber <= totalPages)
      .sort((a, b) => a - b);
  }, [page, totalPages]);

  const openReview = (row) => {
    window.history.pushState({ review: true }, "");
    setActiveView(row);
  };

  const closeReview = () => {
    if (window.history.state?.review) {
      window.history.replaceState({}, "");
    }
    setActiveView(null);
  };

  const updateRequestStatus = (updatedRow, nextStatus, messageToApplicant = "") => {
    const nextDisplayStatus = toDisplayLabel(nextStatus);

    setRequests((currentRequests) =>
      currentRequests.map((request) =>
        isSameRequest(request, updatedRow)
          ? {
              ...request,
              companyStatus: nextStatus,
              messageToApplicant,
              requestStatus: nextStatus,
              status: nextDisplayStatus,
            }
          : request,
      ),
    );

    setActiveView((currentView) =>
      currentView
        ? {
            ...currentView,
            companyStatus: nextStatus,
            messageToApplicant,
            requestStatus: nextStatus,
            status: nextDisplayStatus,
          }
        : currentView,
    );
  };

  if (activeView) {
    return (
      <VendorReviewView
        row={activeView}
        goBack={closeReview}
        onStatusUpdated={updateRequestStatus}
      />
    );
  }

  return (
    <div className="business-request-wrapper">
      <div className="business-request-main-container">
        <div className="business-request-back">
          <span className="business-request-back-arrow"><span className="navigation-direction-icon navigation-direction-glyph">←</span></span>
          <span className="business-request-back-text">{translateLabel("txt_admin_back_to_home", "Back to Home")}</span>
        </div>

        <div className="business-request-top">
          <div className="business-request-title">{translateLabel("txt_admin_business_request", "Business Request")}</div>
          <div className="business-request-subtitle">
            Review and manage vendor applications
          </div>
        </div>

        <div className="business-request-cards">
          <div className="business-request-card">
            <div className="business-request-card-label">
              Total Applications
            </div>
            <div className="business-request-card-value">{totalRecords}</div>
          </div>
          <div className="business-request-card">
            <div className="business-request-card-label">Pending Review</div>
            <div className="business-request-card-value pending">
              {dashboard.pendingReview ?? 0}
            </div>
          </div>
          <div className="business-request-card">
            <div className="business-request-card-label">Active Reviewers</div>
            <div className="business-request-card-value approved">
              {dashboard.activeReviewers ?? 0}
            </div>
          </div>
          <div className="business-request-card">
            <div className="business-request-card-label">
              Today's Submissions
            </div>
            <div className="business-request-card-value">
              {dashboard.todaysSubmissions ?? 0}
            </div>
          </div>
        </div>

        <div className="business-request-toolbar">
          <div className="business-request-search-wrap">
            <span className="business-request-search-icon">
              <SearchIcon />
            </span>
            <input
              className="business-request-search"
              type="text"
              placeholder={translateLabel("txt_admin_search_by_company_or_applicant", "Search by company or applicant...")}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>

          <div className="business-request-filters">
            <select
              className="business-request-filter"
              aria-label={translateLabel("txt_admin_filter_by_business_type", "Filter by business type")}
              value={businessType}
              onChange={(event) => {
                setBusinessType(event.target.value);
                setPage(1);
              }}
            >
              <option value="">{translateLabel("txt_admin_all_business_types", "All business types")}</option>
              {BUSINESS_TYPES.map((type) => (
                <option key={type} value={type}>
                  {toDisplayLabel(type)}
                </option>
              ))}
            </select>

            <select
              className="business-request-filter"
              aria-label={translateLabel("txt_admin_filter_by_request_status", "Filter by request status")}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="">{translateLabel("txt_admin_all_statuses", "All statuses")}</option>
              {REQUEST_STATUSES.map((requestStatus) => (
                <option key={requestStatus} value={requestStatus}>
                  {toDisplayLabel(requestStatus)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="business-request-box">
          <div className="business-request-table-wrap">
            <table className="business-request-table">
              <colgroup>
                <col />
                <col />
                <col />
                <col />
                <col />
                <col />
                <col />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th className="business-request-th business-request-th-check">
                    <input
                      className="business-request-checkbox"
                      type="checkbox"
                      aria-label={translateLabel("txt_admin_select_all_business_requests", "Select all business requests")}
                    />
                  </th>
                  <th className="business-request-th">{translateLabel("input_store_name", "Store Name")}</th>
                  <th className="business-request-th">{translateLabel("input_business_type", "Business Type")}</th>
                  <th className="business-request-th">Owner</th>
                  <th className="business-request-th">Submitted</th>
                  <th className="business-request-th">Documents</th>
                  <th className="business-request-th">{translateLabel("txt_common_status", "Status")}</th>
                  <th className="business-request-th">{translateLabel("txt_common_actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8} className="business-request-empty">
                      Loading business requests...
                    </td>
                  </tr>
                )}

                {!loading && error && (
                  <tr>
                    <td
                      colSpan={8}
                      className="business-request-empty business-request-error"
                    >
                      {translateText(error)}
                    </td>
                  </tr>
                )}

                {!loading &&
                  !error &&
                  requests.map((request) => (
                    <tr className="business-request-tr" key={request.id}>
                      <td className="business-request-td business-request-td-check">
                        <input
                          className="business-request-checkbox"
                          type="checkbox"
                          aria-label={`Select ${request.storeName}`}
                        />
                      </td>
                      <td
                        className="business-request-td business-request-bold business-request-text-cell"
                        title={translateText(request.storeName)}
                      >
                        {request.storeName}
                      </td>
                      <td
                        className="business-request-td business-request-muted business-request-text-cell"
                        title={translateText(request.businessName)}
                      >
                        {request.businessName}
                      </td>
                      <td className="business-request-td business-request-muted">
                        {request.owner}
                      </td>
                      <td className="business-request-td business-request-muted">
                        {request.submitted}
                      </td>
                      <td className="business-request-td">
                        <span className="business-request-docs">
                          <DocumentIcon /> {request.documents}
                        </span>
                      </td>
                      <td className="business-request-td">
                        <StatusBadge status={request.status} />
                      </td>
                      <td className="business-request-td">
                        <button
                          className="business-request-review-btn"
                          type="button"
                          onClick={() => openReview(request)}
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}

                {!loading && !error && requests.length === 0 && (
                  <tr>
                    <td colSpan={8} className="business-request-empty">
                      No business requests found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="business-request-nav">
            <div className="business-request-nav-left">
              <span>{translateLabel("txt_common_rows_per_page", "Rows per page")}</span>
              <div className="business-request-rows-input-wrap">
                <select
                  className="business-request-rows-input"
                  aria-label={translateLabel("txt_common_rows_per_page", "Rows per page")}
                  value={rowsPerPage}
                  onChange={(event) => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <span>of {totalRecords} rows</span>
            </div>

            <div className="business-request-nav-right">
              <button
                className={`business-request-nav-btn ${
                  page === 1 || loading ? "disabled" : ""
                }`}
                type="button"
                aria-label={translateLabel("txt_common_previous_page", "Previous page")}
                disabled={page === 1 || loading}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              ><span className="navigation-direction-icon navigation-direction-glyph">‹</span></button>

              {visiblePages.map((pageNumber, index) => (
                <React.Fragment key={pageNumber}>
                  {index > 0 && pageNumber - visiblePages[index - 1] > 1 && (
                    <span className="business-request-ellipsis">...</span>
                  )}
                  <button
                    className={`business-request-page-num ${
                      page === pageNumber ? "active" : ""
                    }`}
                    type="button"
                    disabled={loading}
                    onClick={() => setPage(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                </React.Fragment>
              ))}

              <button
                className={`business-request-nav-btn ${
                  totalPages === 0 || page === totalPages || loading
                    ? "disabled"
                    : ""
                }`}
                type="button"
                aria-label={translateLabel("txt_common_next_page", "Next page")}
                disabled={totalPages === 0 || page === totalPages || loading}
                onClick={() =>
                  setPage((current) => Math.min(totalPages, current + 1))
                }
              ><span className="navigation-direction-icon navigation-direction-glyph">›</span></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VendorOnboarding;
